import React, { useMemo } from "react";
import PropTypes from "prop-types";
import moment from "moment";

import {
  avgOf,
  weekGroups,
  weekdayGroups,
  toUtcNumber,
  buildPhaseSpans,
  plansInRange,
} from "../../../../../utils/nutrition-progress";
import { CalendarHeader } from "./CalendarHeader";
import { CalendarLegend } from "./CalendarLegend";
import { CalendarGrid } from "./CalendarGrid";
import { CalendarCaption } from "./CalendarCaption";
import { dayShape } from "./CalCell";

// Calendar view. ISO week grid oldest-first (Mon–Sun columns) for
// the reporting period. Periodization bands and Target plans timeline above;
// CalCell per day; right column = weekly avg; bottom row = same-weekday avg.
// Scaffold styling — dark theme comes with design.

/* ---- main view ------------------------------------------------------- */

const CalendarView = ({
  dayRows = [],
  weekGridDayRows,
  dateFrom,
  dateTo,
  nutritionPlans = [],
  athlete,
  hasPlanner = true,
  onDayClick,
}) => {
  const fromUtc = useMemo(() => toUtcNumber(dateFrom), [dateFrom]);
  const toUtc = useMemo(() => toUtcNumber(dateTo), [dateTo]);

  /**
   * Creates a map of days to their data. weekGridDayRows widens dayRows to
   * the full ISO week span (leading/trailing padding days outside the
   * reporting period) so grid cells there can show real stats too — dayRows
   * itself stays untouched everywhere else so rollups stay period-scoped.
   */
  const dayMap = useMemo(
    () => new Map((weekGridDayRows ?? dayRows).map((d) => [d.utcDate, d])),
    [weekGridDayRows, dayRows]
  );

  /**
   * Finds the plans that are visible in the calendar view.
   */
  const planStartDates = useMemo(
    () => new Set(nutritionPlans.map((p) => p.utcStartDate)),
    [nutritionPlans]
  );
  /**
   * Finds the plans that are visible in the calendar view.
   */
  const visiblePlans = useMemo(
    () =>
      plansInRange(nutritionPlans, fromUtc, toUtc).sort(
        (a, b) => a.utcStartDate - b.utcStartDate
      ),
    [nutritionPlans, fromUtc, toUtc]
  );

  // Memoized here so CalendarHeader stays allocation-free — no buildPhaseSpans call inside it.
  const phaseSpans = useMemo(
    () => hasPlanner ? buildPhaseSpans(athlete, fromUtc, toUtc) : [],
    [hasPlanner, athlete, fromUtc, toUtc]
  );
  const nutritionPhaseSpans = useMemo(
    () =>
      hasPlanner
        ? buildPhaseSpans(athlete, fromUtc, toUtc, "nutritionPhases")
        : [],
    [hasPlanner, athlete, fromUtc, toUtc]
  );

  const weekBuckets = useMemo(() => weekGroups(dayRows).reverse(), [dayRows]);
  // Weekday-of-period average row (below the grid) — approved by the lead,
  // re-enabled after being scaffolded-but-commented pending design sign-off.
  // avgOf resolved here (not inside WeekdayAvgRow) so it only recomputes
  // when dayRows actually changes, not on every render of that row.
  const weekdayAvgs = useMemo(
    () => weekdayGroups(dayRows).map((rows) => avgOf(rows)),
    [dayRows]
  );
  const overallAvg = useMemo(() => avgOf(dayRows), [dayRows]);
  const loggedDays = useMemo(() => dayRows.filter((d) => d.logged).length, [dayRows]);
  const firstUtcDate = dayRows.length ? dayRows.at(-1).utcDate : fromUtc;

  // Expand each ISO week bucket into 7 Mon–Sun slots. Days outside the
  // reporting range still render like any other day (kcal chip if logged,
  // "not logged" otherwise) — outOfRange only suppresses report-level
  // markers (isFirstDay / "new plan" pill) that shouldn't apply outside the
  // period, not the cell's own content. Also finds the first in-range cell
  // of the row (firstCellIndex) — CalCell uses it to decide whether to show
  // "MMM D" instead of a bare day number, so every row self-identifies its
  // month without a left-hand week-label column (see CalCell's showMonth).
  const weekRows = useMemo(
    () =>
      weekBuckets.map((bucketDays) => {
        const monday = Number(
          moment(String(bucketDays[0].utcDate), "YYYYMMDD")
            .startOf("isoWeek")
            .format("YYYYMMDD")
        );
        const cells = Array.from({ length: 7 }, (_, i) => {
          const utcDate = Number(
            moment(String(monday), "YYYYMMDD").add(i, "days").format("YYYYMMDD")
          );
          const inRange = utcDate >= fromUtc && utcDate <= toUtc;
          const found = dayMap.get(utcDate);
          if (inRange) return found ?? { utcDate, outOfRange: false };
          return found ? { ...found, outOfRange: true } : { utcDate, outOfRange: true };
        });
        const avg = avgOf(bucketDays);
        const uniquePlans = new Set(bucketDays.filter((d) => d.planId).map((d) => d.planId));
        const spansPlans = uniquePlans.size > 1 ? uniquePlans.size : null;
        const firstCellIndex = cells.findIndex((c) => !c.outOfRange);
        return { monday, cells, avg, spansPlans, firstCellIndex };
      }),
    [weekBuckets, dayMap]
  );

  return (
    <div>
      <CalendarHeader
        fromUtc={fromUtc}
        toUtc={toUtc}
        totalDays={dayRows.length}
        loggedDays={loggedDays}
        phaseSpans={phaseSpans}
        nutritionPhaseSpans={nutritionPhaseSpans}
        visiblePlans={visiblePlans}
      />

      <CalendarLegend />

      <CalendarGrid
        weekRows={weekRows}
        planStartDates={planStartDates}
        firstUtcDate={firstUtcDate}
        weekdayAvgs={weekdayAvgs}
        overallAvg={overallAvg}
        onDayClick={onDayClick}
      />

      <CalendarCaption />
    </div>
  );
};

CalendarView.propTypes = {
  dayRows: PropTypes.arrayOf(dayShape),
  weekGridDayRows: PropTypes.arrayOf(dayShape),
  dateFrom: PropTypes.instanceOf(Date),
  dateTo: PropTypes.instanceOf(Date),
  nutritionPlans: PropTypes.arrayOf(PropTypes.object),
  athlete: PropTypes.object,
  hasPlanner: PropTypes.bool,
  onDayClick: PropTypes.func,
};

export default CalendarView;
